'use client';

import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { formatPrice } from '@/utils/number';
import { cn } from '@/utils/cn';
import { getImageUrl, getProductTitleNameWithElement } from '@/utils/string';
import Button from '@/app/components/Button';
import { getCheckoutUrl } from '@/url/checkout';
import { useRouter } from 'next/navigation';
import { buyNowAsync } from '@/redux/thunk/carts-thunk';
import { resetCheckoutSummaryState } from '@/redux/reducers/checkout-summary-reducer';
import { imageTypes, orderCheckoutTypes, screenTypes } from '@/constants/types';
import Link from 'next/link';
import { getProductDetailUrl } from '@/url/product';
import useWindowDimensions from '@/app/hooks/useWindowSize';
import { resetPlaceOrderStateForNextCheckout } from '@/redux/reducers/place-order-reducer';
import { resetBreadcrumb } from '@/redux/reducers/breadcrumb-reducer';
import { resetShippingInfoAfterOrderSucceeded } from '@/redux/reducers/shipping-address-reducer';
import { prepareProductDetailSlug } from '@/utils/object';
import { updateSelectStore } from '@/redux/reducers/store-locator-reducer';
import LoadingSpinnerV2 from '../Skeleton/LoadingSpinnerV2';
import ImageComponentV2 from '../Image/ImageComponentV2';
import TextXl from '../Typography/TextXl';
import TextLarge from '../Typography/TextLarge';
import TextSmall from '../Typography/TextSmall';
import Badge from '../Badge';
import ButtonLabel from '../Typography/ButtonLabel';

function HomepageFocusProductCard({
  productImages = [],
  price = '',
  save = '',
  productId,
  product = null,
}) {
  const dispatch = useDispatch();
  const router = useRouter();
  const { width: screenWidth } = useWindowDimensions();

  const { isLoading: cartLoading } = useSelector((state) => state.carts);

  const [isLoading, setIsLoading] = useState(false);

  const [hover, setHover] = useState(false);

  const handleBuyNow = async (e) => {
    setIsLoading(true);
    e.preventDefault();
    e.stopPropagation();
    dispatch(resetShippingInfoAfterOrderSucceeded());
    dispatch(resetCheckoutSummaryState(orderCheckoutTypes.buyNow));
    dispatch(resetPlaceOrderStateForNextCheckout());
    dispatch(updateSelectStore(null));

    try {
      await dispatch(
        buyNowAsync({ product: { id: productId, cart_quantity: 1 } }),
      )
        .unwrap()
        .then(() => {
          router.push(getCheckoutUrl());
        })
        .catch((error) => {
          console.error('Buy now failed:', error);
        })
        .finally(() => {
          setIsLoading(false);
        });
    } catch (error) {
      console.error('Exception occurred:', error);
      setIsLoading(false);
    }
  };

  const handleDetailsView = (e) => {
    if (
      e.ctrlKey || // Windows/Linux: Ctrl + click
      e.metaKey || // Mac: Cmd + click
      e.button === 1 // Middle mouse button
    ) {
      return;
    }

    dispatch(resetBreadcrumb());

    router.push(getProductDetailUrl(prepareProductDetailSlug(product)));
  };

  const fullName = getProductTitleNameWithElement(product);

  return (
    <Link
      prefetch={false}
      aria-label="Go to product details"
      className="homepage-focus-product-card w-full max-h-[672px] lg:w-1/2 bg-pale-grey shadow-what-are-you-looking-for-box-shadow rounded-8 lg:rounded-xl group overflow-hidden hover:shadow-card-hover transition-colors duration-100"
      passHref
      href={getProductDetailUrl(prepareProductDetailSlug(product))}
      onClick={(e) => handleDetailsView(e)}
    >
      <div
        className="w-full h-full flex flex-col items-center justify-between lg:gap-y-6"
        onMouseEnter={() =>
          setTimeout(() => {
            setHover(true);
          }, 10)
        }
        onMouseLeave={() =>
          setTimeout(() => {
            setHover(false);
          }, 10)
        }
      >
        <div className="focus-card-image-container w-full h-[260px] lg:h-[475px] max-h-[475px] mb-8 lg:mb-0 relative overflow-hidden flex justify-center">
          {productImages?.wide ? (
            <ImageComponentV2
              src={
                productImages?.wide[0]?.image
                  ? getImageUrl(productImages?.wide[0]?.image)
                  : null
              }
              alt={productImages?.name?.en}
              className="h-full max-w-full object-contain object-center"
              type={imageTypes.wide}
              width={600}
              height={475}
              loading="lazy"
            />
          ) : (
            <ImageComponentV2
              src={null}
              alt={productImages?.name?.en}
              className="h-full max-w-full object-contain object-center"
              type={imageTypes.wide}
              width={600}
              height={475}
              loading="lazy"
            />
          )}
        </div>

        <div className="focus-card-content max-h-[108px] lg:max-h-[128px] p-[8px] lg:p-[24px] w-full">
          <TextXl
            text={fullName}
            className="text-coarse-wool text-center font-semibold line-clamp-1 mb-12 lg:mb-16"
          />

          <div>
            <div
              className={cn(
                'flex flex-row lg:flex-row items-center justify-center mb-12 lg:mb-0 transition-all duration-300',
                { 'lg:-translate-y-5 lg:invisible lg:opacity-0': hover },
              )}
            >
              <TextLarge
                text={`৳ ${formatPrice(price)}`}
                className="text-sm lg:text-lg text-richcarmine font-semibold"
              />

              {save !== '' && (
                <div className="flex items-center">
                  <TextSmall
                    text="Save"
                    className="text-xs lg:text-sm text-riverbed font-semibold ml-4 lg:ml-8 mr-4"
                  />

                  <Badge type="discount-offer-badge" offerText={save} />
                </div>
              )}
            </div>

            <div
              role="button"
              tabIndex={0}
              aria-label="top-product-card-action"
              className="w-full"
              onClick={(e) => e.stopPropagation()}
            >
              {product?.limited_stock ? (
                <Button
                  type="see-details"
                  className={cn(
                    'w-[100px] lg:w-[200px] visible lg:invisible mx-auto !px-16 !py-4 lg:!py-10 opacity-100 lg:opacity-0 transition-all duration-300 text-nowrap',
                    { 'lg:-translate-y-8 lg:!visible lg:!opacity-100': hover },
                  )}
                  isShowIcon={window.innerWidth > screenTypes.large}
                  onClick={(e) => handleDetailsView(e)}
                />
              ) : (
                <Button
                  onClick={(e) => handleBuyNow(e)}
                  type="primary"
                  className={cn(
                    'w-[100px] lg:w-[200px] !bg-pacific-queen visible lg:invisible mx-auto !px-16 !py-4 lg:!py-10 opacity-100 lg:opacity-0 transition-all duration-300 hover:!bg-cape-verde lg:h-[36px]',
                    { 'lg:-translate-y-8 lg:!visible lg:!opacity-100': hover },
                    {
                      'cursor-not-allowed !bg-aquarelle-purple !border-himalayan-poppy hover:!bg-aquarelle-purple hover:!border-himalayan-poppy ':
                        isLoading && cartLoading,
                    },
                  )}
                  disabled={isLoading && cartLoading}
                >
                  {isLoading && cartLoading ? (
                    <LoadingSpinnerV2
                      size={screenWidth >= screenTypes.large ? '24' : '14'}
                      className="bg-transparent"
                    />
                  ) : (
                    <ButtonLabel
                      label="Buy Now"
                      className="text-xs lg:text-sm text-white text-center font-semibold lg:font-bold line-clamp-2"
                    />
                  )}
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>
    </Link>
  );
}

export default HomepageFocusProductCard;
